"use client";

import { c as _c } from "react/compiler-runtime";
import CollectionsDropdown from "@/components/collections-dropdown/CollectionsDropdown";
import DotLoader from "@/components/dotLoader/DotLoader";
import { LFGButton } from "@/components/lfg-slideshow/LFGSlideshow";
import RememeImage from "@/components/nft-image/RememeImage";
import NothingHereYetSummer from "@/components/nothingHereYet/NothingHereYetSummer";
import Pagination from "@/components/pagination/Pagination";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import type { CommonSelectItem } from "@/components/utils/select/CommonSelect";
import CommonDropdown from "@/components/utils/select/dropdown/CommonDropdown";
import { publicEnv } from "@/config/env";
import { useSetTitle } from "@/contexts/TitleContext";
import type { DBResponse } from "@/entities/IDBResponse";
import type { NFTLite, Rememe } from "@/entities/INFT";
import { formatAddress, numberWithCommas } from "@/helpers/Helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { fetchUrl } from "@/services/6529api";
import { ArrowPathIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
const PAGE_SIZE = 40;
enum TokenType {
  ALL = "All",
  ERC721 = "ERC-721",
  ERC1155 = "ERC-1155",
}
export enum RememeSort {
  RANDOM = "Random",
  CREATED_ASC = "Recently Added",
}
const REMEMES_GRID_CLASS = "tw-grid tw-grid-cols-2 tw-gap-3 tw-pt-2 sm:tw-grid-cols-3 sm:tw-gap-4 lg:tw-grid-cols-4 xl:tw-gap-5";
const REMEMES_TOTAL_COUNT_CLASS = "tw-shrink-0 tw-text-sm tw-font-medium tw-leading-none tw-text-iron-500 sm:tw-text-base";
function getRememeCollectionName(rememe: Rememe) {
  return rememe.contract_opensea_data?.collectionName?.trim() || formatAddress(rememe.contract);
}
function getRememeTitle(rememe: Rememe) {
  const metadataName = typeof rememe.metadata?.name === "string" ? rememe.metadata.name.trim() : "";
  return metadataName || getRememeCollectionName(rememe);
}
export default function Rememes() {
  const $ = _c(79);
  useSetTitle("ReMemes | Collections");
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [memes, setMemes] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [rememes, setRememes] = useState(t1);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [rememesLoaded, setRememesLoaded] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [TokenType.ALL, TokenType.ERC721, TokenType.ERC1155];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tokenTypes = t2;
  const [selectedTokenType, setSelectedTokenType] = useState(TokenType.ALL);
  let queryMemeId;
  let t3;
  let t4;
  if ($[3] !== searchParams) {
    queryMemeId = searchParams.get("meme_id");
    const parsedQueryMemeId = queryMemeId ? Number.parseInt(queryMemeId) : 0;
    t3 = useState;
    t4 = Number.isFinite(parsedQueryMemeId) ? parsedQueryMemeId : 0;
    $[3] = searchParams;
    $[4] = queryMemeId;
    $[5] = t3;
    $[6] = t4;
  } else {
    queryMemeId = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  const [selectedMeme, setSelectedMeme] = t3(t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [RememeSort.RANDOM, RememeSort.CREATED_ASC];
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const sorting = t5;
  const [selectedSorting, setSelectedSorting] = useState(RememeSort.RANDOM);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = sorting.map(_temp);
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const sortingItems = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = tokenTypes.map(_temp2);
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const tokenTypeItems = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      label: "All",
      value: 0,
      key: "meme-all"
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== memes) {
    t9 = [t8, ...memes.map(_temp3)];
    $[11] = memes;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const memeReferenceItems = t9;
  const activeFetchRequest = useRef(null);
  let t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      fetchUrl(`${publicEnv.API_ENDPOINT}/api/memes_lite`).then(response => {
        setMemes(response.data);
      }).catch(_temp4);
    };
    t11 = [];
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  useEffect(t10, t11);
  let t12;
  if ($[15] !== selectedMeme || $[16] !== selectedSorting || $[17] !== selectedTokenType) {
    t12 = mypage => {
      setRememesLoaded(false);
      activeFetchRequest.current?.abort();
      const abortController = new AbortController();
      activeFetchRequest.current = abortController;
      let memeFilter = "";
      if (selectedMeme) {
        memeFilter = `&meme_id=${selectedMeme}`;
      }
      let tokenTypeFilter = "";
      if (selectedTokenType !== TokenType.ALL) {
        tokenTypeFilter = `&token_type=${selectedTokenType.replaceAll("-", "")}`;
      }
      let sort_0 = "";
      if (selectedSorting === RememeSort.CREATED_ASC) {
        sort_0 = "&sort=created_at&sort_direction=desc";
      }
      const url = `${publicEnv.API_ENDPOINT}/api/rememes?page_size=${PAGE_SIZE}&page=${mypage}${memeFilter}${tokenTypeFilter}${sort_0}`;
      fetchUrl(url, {
        signal: abortController.signal
      }).then(response_0 => {
        if (abortController.signal.aborted || activeFetchRequest.current !== abortController) {
          return;
        }
        setTotalResults(response_0.count);
        setRememes(response_0.data);
      }).catch(err => {
        if (abortController.signal.aborted) {
          return;
        }
        console.error("Error fetching rememes", err);
      }).finally(() => {
        if (activeFetchRequest.current === abortController) {
          activeFetchRequest.current = null;
          setRememesLoaded(true);
        }
      });
    };
    $[15] = selectedMeme;
    $[16] = selectedSorting;
    $[17] = selectedTokenType;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const fetchResults = t12;
  let t13;
  if ($[19] !== selectedMeme || $[20] !== selectedSorting || $[21] !== selectedTokenType) {
    t13 = {
      selectedMeme,
      selectedSorting,
      selectedTokenType
    };
    $[19] = selectedMeme;
    $[20] = selectedSorting;
    $[21] = selectedTokenType;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const previousFilters = useRef(t13);
  let t14;
  let t15;
  if ($[23] !== pathname || $[24] !== router || $[25] !== searchParams || $[26] !== selectedMeme) {
    t14 = () => {
      const nextSearchParams = new URLSearchParams(searchParams.toString());
      if (selectedMeme) {
        nextSearchParams.set("meme_id", selectedMeme.toString());
      } else {
        nextSearchParams.delete("meme_id");
      }
      const currentQuery = searchParams.toString();
      const nextQuery = nextSearchParams.toString();
      if (nextQuery !== currentQuery) {
        const nextPath = nextQuery ? `${pathname}?${nextQuery}` : pathname;
        router.replace(nextPath, {
          scroll: false
        });
      }
    };
    t15 = [pathname, router, searchParams, selectedMeme];
    $[23] = pathname;
    $[24] = router;
    $[25] = searchParams;
    $[26] = selectedMeme;
    $[27] = t14;
    $[28] = t15;
  } else {
    t14 = $[27];
    t15 = $[28];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[29] !== fetchResults || $[30] !== page || $[31] !== selectedMeme || $[32] !== selectedSorting || $[33] !== selectedTokenType) {
    t16 = () => {
      const filtersChanged = previousFilters.current.selectedMeme !== selectedMeme || previousFilters.current.selectedSorting !== selectedSorting || previousFilters.current.selectedTokenType !== selectedTokenType;
      previousFilters.current = {
        selectedMeme,
        selectedSorting,
        selectedTokenType
      };
      if (filtersChanged && page !== 1) {
        setPage(1);
        return;
      }
      fetchResults(page);
      return () => {
        activeFetchRequest.current?.abort();
        activeFetchRequest.current = null;
      };
    };
    t17 = [fetchResults, page, selectedMeme, selectedSorting, selectedTokenType];
    $[29] = fetchResults;
    $[30] = page;
    $[31] = selectedMeme;
    $[32] = selectedSorting;
    $[33] = selectedTokenType;
    $[34] = t16;
    $[35] = t17;
  } else {
    t16 = $[34];
    t17 = $[35];
  }
  useEffect(t16, t17);
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = function printRememe(rememe) {
      const collectionName = getRememeCollectionName(rememe);
      const replicaCount = rememe.replicas.length;
      return <a key={`${rememe.contract}-${rememe.id}`} href={`/rememes/${rememe.contract}/${rememe.id}`} className="tw-group tw-flex tw-h-full tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-border-white/20 hover:tw-bg-iron-900/50 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-primary-400"><div className="tw-bg-iron-900"><RememeImage nft={rememe} animation={false} height={300} /></div><div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-px-2 tw-pb-4 tw-pt-4 tw-text-center md:tw-px-4"><div className="tw-line-clamp-2 tw-w-full tw-max-w-full tw-break-words tw-text-center tw-text-sm tw-font-semibold tw-leading-snug tw-text-iron-50 md:tw-text-md">{getRememeTitle(rememe)}</div><div className="tw-mt-2 tw-flex tw-min-h-5 tw-w-full tw-min-w-0 tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-1.5 tw-gap-y-0.5 tw-text-center tw-text-xs tw-leading-5 tw-text-iron-500"><span className="tw-break-words">{collectionName}{replicaCount > 1 && <> (x{numberWithCommas(replicaCount)})</>}</span><span aria-hidden="true" className="tw-shrink-0 tw-text-iron-600">·</span><span aria-label={`Token #${rememe.id}`} className="tw-min-w-0 tw-truncate tw-font-medium">#{rememe.id}</span></div></div></a>;
    };
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const printRememe = t18;
  let t19;
  if ($[37] !== rememes) {
    t19 = function printRememes() {
      if (rememes.length === 0) {
        return <div><NothingHereYetSummer /></div>;
      }
      return <div className={REMEMES_GRID_CLASS}>{rememes.map(printRememe)}</div>;
    };
    $[37] = rememes;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const printRememes = t19;
  let t20;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Image unoptimized={true} loading="eager" width="0" height="0" style={{
      width: "250px",
      height: "auto"
    }} className="tw-hidden md:tw-block" src="/re-memes.png" alt="re-memes" />;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== totalResults) {
    t21 = totalResults > 0 && <span className={REMEMES_TOTAL_COUNT_CLASS}>(x{numberWithCommas(totalResults)})</span>;
    $[40] = totalResults;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t21) {
    t22 = <div className="tw-hidden tw-min-w-0 tw-items-center tw-gap-2 xl:tw-flex">{t20}{t21}</div>;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Image unoptimized={true} loading="eager" width="0" height="0" style={{
      width: "150px",
      height: "auto"
    }} className="tw-shrink tw-basis-auto" src="/re-memes.png" alt="re-memes" />;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== totalResults) {
    t24 = totalResults > 0 && <span className={REMEMES_TOTAL_COUNT_CLASS}>(x{numberWithCommas(totalResults)})</span>;
    $[45] = totalResults;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t24) {
    t25 = <div className="tw-min-w-0 xl:tw-hidden"><CollectionsDropdown activePage="rememes" variant="brand" triggerContent={<span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-2">{t23}{t24}</span>} /></div>;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <LFGButton contract="rememes" />;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t22 || $[51] !== t25) {
    t27 = <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-2 sm:tw-w-auto sm:tw-justify-start">{t22}{t25}{t26}</div>;
    $[50] = t22;
    $[51] = t25;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="tw-flex tw-w-full tw-items-center sm:tw-w-auto sm:tw-justify-end"><div className="tw-w-full sm:tw-w-auto [&>button]:tw-w-full sm:[&>button]:tw-w-auto"><PrimaryButton loading={false} disabled={false} onClicked={_temp5}>Add ReMeme<PlusCircleIcon className="tw-size-4 tw-shrink-0 [stroke-width:2.25]" /></PrimaryButton></div></div>;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t27) {
    t29 = <header className="tw-pb-5"><div className="tw-flex tw-flex-col tw-gap-4"><div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">{t27}{t28}</div></div></header>;
    $[54] = t27;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== fetchResults || $[57] !== memeReferenceItems || $[58] !== page || $[59] !== queryMemeId || $[60] !== rememesLoaded || $[61] !== selectedMeme || $[62] !== selectedSorting || $[63] !== selectedTokenType) {
    t30 = rememesLoaded && <div className="tw-flex tw-min-w-0 tw-flex-nowrap tw-items-center tw-gap-x-6 tw-gap-y-2 tw-overflow-x-auto tw-overflow-y-hidden tw-py-2 tw-pb-3 [-ms-overflow-style:none] [scrollbar-width:none] sm:tw-flex-wrap sm:tw-overflow-visible sm:tw-pb-2 md:tw-justify-between [&::-webkit-scrollbar]:tw-hidden"><div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2"><CommonDropdown items={sortingItems} activeItem={selectedSorting} filterLabel="Sort" setSelected={setSelectedSorting} size="sm" variant="editorial" showFilterLabel={true} />{selectedSorting === RememeSort.RANDOM && <><button type="button" aria-label="Refresh results" onClick={() => fetchResults(page)} data-tooltip-id="refresh-rememes-results" className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-iron-400 tw-transition hover:tw-bg-white/[0.04] hover:tw-text-iron-200 focus:tw-outline-none focus-visible:tw-outline-none"><ArrowPathIcon className="tw-size-5" /></button><Tooltip id="refresh-rememes-results" place="top" opacity={1} style={TOOLTIP_STYLES}>Refresh results</Tooltip></>}</div><div className="tw-flex tw-shrink-0 tw-flex-nowrap tw-items-center tw-gap-x-6 tw-gap-y-2 sm:tw-flex-wrap md:tw-ml-auto"><CommonDropdown items={tokenTypeItems} activeItem={selectedTokenType} filterLabel="Token Type" setSelected={setSelectedTokenType} size="sm" variant="editorial" showFilterLabel={true} /><div className="tw-shrink-0"><CommonDropdown items={memeReferenceItems} activeItem={selectedMeme} filterLabel="Meme Reference" noneLabel={queryMemeId ?? undefined} setSelected={setSelectedMeme} size="sm" variant="editorial" menuMinWidth={320} showFilterLabel={true} /></div></div></div>;
    $[56] = fetchResults;
    $[57] = memeReferenceItems;
    $[58] = page;
    $[59] = queryMemeId;
    $[60] = rememesLoaded;
    $[61] = selectedMeme;
    $[62] = selectedSorting;
    $[63] = selectedTokenType;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== printRememes || $[66] !== rememesLoaded) {
    t31 = rememesLoaded ? printRememes() : <div className="tw-pt-3">Fetching <DotLoader /></div>;
    $[65] = printRememes;
    $[66] = rememesLoaded;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t29 || $[69] !== t30 || $[70] !== t31) {
    t32 = <div className="tw-mx-auto tw-w-full tw-max-w-[1400px] tw-px-4 tw-py-6 md:tw-px-6 md:tw-py-10 lg:tw-px-8">{t29}{t30}{t31}</div>;
    $[68] = t29;
    $[69] = t30;
    $[70] = t31;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== page || $[73] !== rememesLoaded || $[74] !== totalResults) {
    t33 = totalResults > PAGE_SIZE && rememesLoaded && <div className="tw-py-4 tw-text-center"><Pagination page={page} pageSize={PAGE_SIZE} totalResults={totalResults} setPage={function (newPage) {
        setPage(newPage);
        window.scrollTo(0, 0);
      }} /></div>;
    $[72] = page;
    $[73] = rememesLoaded;
    $[74] = totalResults;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== t32 || $[77] !== t33) {
    t34 = <div className="tailwind-scope tw-min-h-[calc(100vh-100px)] tw-bg-[#0D0D0F] tw-pb-5 tw-text-white">{t32}{t33}</div>;
    $[76] = t32;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  return t34;
}
function _temp5() {
  window.location.href = "/rememes/add";
}
function _temp4(error) {
  console.error("Error fetching memes", error);
}
function _temp3(meme) {
  return {
    label: `#${meme.id} - ${meme.name}`,
    value: meme.id,
    key: `meme-${meme.id}`
  };
}
function _temp2(tokenType) {
  return {
    label: tokenType,
    value: tokenType,
    key: `token-type-${tokenType}`
  };
}
function _temp(sort) {
  return {
    label: sort,
    value: sort,
    key: `sorting-${sort}`
  };
}